/**
 * Documents API client + upload orchestration (§7g).
 *
 * The upload flow is three-step and bundled into `uploadFile()` so
 * useStore.uploadDocument is a single async call:
 *   1. POST .../documents/upload-url → pre-signed PUT URLs + metadata body
 *   2. PUT the file to uploadUrl with the declared ContentType
 *   3. PUT the metadata sidecar JSON to metadataUploadUrl
 *   4. POST .../documents/sync to trigger KB ingestion
 *
 * The backend's /upload-url endpoint returns the exact metadata shape
 * we need to PUT — we never construct it client-side. This is the 7b
 * design: metadata contract has a single owner, the projects Lambda.
 */

import { request } from "./api.js";

export const listDocuments = (projectId) =>
  request(`/api/projects/${projectId}/documents`);

// Filename goes in the query string rather than the path because
// research-output filenames contain `/` (e.g.
// `agent-outputs/research/0_shared_research.md`), and API Gateway HTTP API
// path params are single-segment — a path-param route never matches and
// the browser sees the 404 as a CORS NetworkError. See app.py delete route.
// Same query-string pattern as delete — research-output filenames contain
// `/` which can't appear in an API Gateway HTTP API path param.
export const createDownloadUrl = (projectId, filename) =>
  request(
    `/api/projects/${projectId}/documents/download?filename=${encodeURIComponent(filename)}`,
  );

/**
 * Fetch a pre-signed download URL and trigger a browser download.
 *
 * We can't just navigate to the signed URL because the API returns a JSON
 * envelope — the signed URL is nested inside it. The anchor-click pattern
 * keeps the user on the same tab and honours the Content-Disposition
 * attachment header set by the backend.
 */
export async function downloadFile(projectId, filename) {
  const { downloadUrl } = await createDownloadUrl(projectId, filename);
  const a = document.createElement("a");
  a.href = downloadUrl;
  // Fall back to the filename's last segment for research outputs that
  // contain `/` in the stored path.
  a.download = filename.split("/").pop() || filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export const deleteDocument = (projectId, filename) =>
  request(
    `/api/projects/${projectId}/documents?filename=${encodeURIComponent(filename)}`,
    { method: "DELETE" },
  );

// §15.4 Editor endpoints. Filename travels as a query param for the same
// reason as download/delete — research-output paths contain `/`.

export const loadEditorDocument = (projectId, filename) =>
  request(
    `/api/projects/${projectId}/documents/editor?filename=${encodeURIComponent(filename)}`,
  );

export const lockDocument = (projectId, filename) =>
  request(
    `/api/projects/${projectId}/documents/lock?filename=${encodeURIComponent(filename)}`,
    { method: "POST" },
  );

export const unlockDocument = (projectId, filename) =>
  request(
    `/api/projects/${projectId}/documents/lock?filename=${encodeURIComponent(filename)}`,
    { method: "DELETE" },
  );

/**
 * Save the editor buffer back to S3 via the pre-signed upload flow.
 * Reuses createUploadUrl to avoid duplicating the metadata contract.
 */
export async function saveEditorDocument(projectId, filename, content) {
  const contentType = filename.endsWith(".md") ? "text/markdown" : "text/plain";
  const urls = await createUploadUrl(projectId, { filename, contentType });

  const fileResp = await fetch(urls.uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": contentType },
    body: content,
  });
  if (!fileResp.ok) {
    throw new Error(
      `Failed to save document: ${fileResp.status} ${fileResp.statusText}`,
    );
  }

  const metaResp = await fetch(urls.metadataUploadUrl, {
    method: "PUT",
    headers: { "Content-Type": urls.metadataContentType },
    body: JSON.stringify(urls.metadataBody),
  });
  if (!metaResp.ok) {
    throw new Error("Saved but metadata write failed — please retry.");
  }

  try {
    await syncDocuments(projectId);
  } catch (err) {
    console.warn(`KB sync after save failed (non-fatal): ${err.message}`);
  }

  return { filename, savedAt: new Date().toISOString() };
}

export const createUploadUrl = (projectId, { filename, contentType }) =>
  request(`/api/projects/${projectId}/documents/upload-url`, {
    method: "POST",
    body: JSON.stringify({ filename, contentType }),
  });

export const syncDocuments = (projectId) =>
  request(`/api/projects/${projectId}/documents/sync`, { method: "POST" });

/**
 * Delete an orphan file + sidecar after the pre-signed upload flow failed
 * mid-sequence (G7). The pre-signed URLs grant PUT only, so the Lambda
 * has to do the cleanup on the browser's behalf.
 */
export const cancelUpload = (projectId, s3Key) =>
  request(`/api/projects/${projectId}/documents/cancel-upload`, {
    method: "POST",
    body: JSON.stringify({ s3Key }),
  });

/**
 * Generate a PPTX/XLSX from a locked markdown document (§25.1).
 *
 * Uses the query-string route variant because research-output filenames
 * contain `/` which can't travel in an API Gateway HTTP API path param —
 * same rationale as download/delete.
 *
 * Returns `{ downloadUrl, filename }` — caller is responsible for
 * triggering the browser download (pattern mirrors downloadFile).
 */
export const generateDocument = (projectId, filename, format) =>
  request(
    `/api/projects/${projectId}/documents/generate?filename=${encodeURIComponent(filename)}`,
    {
      method: "POST",
      body: JSON.stringify({ format }),
    },
  );

/** Generate the document and trigger a browser download of the result. */
export async function generateAndDownload(projectId, filename, format) {
  const { downloadUrl, filename: generatedName } = await generateDocument(
    projectId,
    filename,
    format,
  );
  const a = document.createElement("a");
  a.href = downloadUrl;
  a.download = (generatedName || filename).split("/").pop();
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/**
 * Upload a File to a project by orchestrating the pre-signed URL flow.
 *
 * Throws on any step failure — caller should surface the error to the
 * user. On success, the backend has the file in S3 and has scheduled a
 * KB ingestion; the file may take up to a minute to become retrievable
 * in chat (optimistic "ready" per our §7.8 Q9 decision).
 *
 * @param {string} projectId
 * @param {File} file — browser File object from a dropzone or input
 * @returns {Promise<{filename: string, s3Key: string}>}
 */
export async function uploadFile(projectId, file) {
  const contentType = file.type || "application/octet-stream";

  const urls = await createUploadUrl(projectId, {
    filename: file.name,
    contentType,
  });

  // Step 1: the file itself
  const fileResp = await fetch(urls.uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": contentType },
    body: file,
  });
  if (!fileResp.ok) {
    throw new Error(
      `Failed to upload file to S3: ${fileResp.status} ${fileResp.statusText}`,
    );
  }

  // Step 2: the metadata sidecar. The backend returns the exact body
  // we should PUT — we never construct it ourselves, so the KB metadata
  // contract has a single owner (the projects Lambda, per 7b design).
  //
  // Orphan cleanup (G7): the file PUT already landed, so any failure
  // from here on — either a non-2xx response OR a thrown fetch error
  // (connection drop, CORS flake, DNS failure) — must trigger the
  // Lambda-assisted DELETE. Pre-signed URLs grant PUT, not DELETE, so
  // the browser can't clean up on its own. A try/catch around the
  // fetch is required because a rejected fetch would otherwise escape
  // before the cleanup branch runs — and a transient network error is
  // the likelier failure mode than a non-OK HTTP response.
  let metaOk = false;
  let metaError = null;
  try {
    const metaResp = await fetch(urls.metadataUploadUrl, {
      method: "PUT",
      headers: { "Content-Type": urls.metadataContentType },
      body: JSON.stringify(urls.metadataBody),
    });
    metaOk = metaResp.ok;
    if (!metaOk) {
      metaError = `${metaResp.status} ${metaResp.statusText}`;
    }
  } catch (err) {
    metaError = err.message || "network error";
  }
  if (!metaOk) {
    // Swallow cleanup failures — the user already sees an upload-failed
    // toast and a backend janitor / manual cleanup will handle any
    // residue. Re-throwing the cleanup error would mask the real cause.
    try {
      await cancelUpload(projectId, urls.s3Key);
    } catch (cleanupErr) {
      console.warn(
        `Orphan cleanup failed (non-fatal): ${cleanupErr.message}`,
      );
    }
    throw new Error(
      `File uploaded but metadata write failed (${metaError}) — please retry the upload.`,
    );
  }

  // Step 3: trigger KB ingestion. Swallow 202/409 as success — 409
  // means a sync was already in flight (will pick up our file on the
  // next cycle).
  try {
    await syncDocuments(projectId);
  } catch (err) {
    // Non-fatal: the file is in S3. The KB will sync on the next
    // manual trigger or the next successful upload. Log and proceed.
    console.warn(`KB sync after upload failed (non-fatal): ${err.message}`);
  }

  return { filename: file.name, s3Key: urls.s3Key };
}
